// Third-party packages
// The `/bundled` flavor inlines every SDK plugin (XhrQueue, consent,
// device-mode…) instead of dynamic-importing them from a remote URL at
// runtime. The remote path only works UNBUNDLED (CDN script tag): any
// consumer bundler (rspack/webpack/vite) rewrites the SDK's dynamic
// `import(url)` into its own chunk loader, which cannot resolve an https
// URL — every plugin then fails with "Cannot find module" and no event is
// ever sent (XhrQueue is the delivery pipeline). Bundling the plugins makes
// both distributions self-contained and drops a runtime CDN dependency.
import {
	RudderAnalytics,
} from '@rudderstack/analytics-js/bundled';

// Internal libraries (@stonal-tech/*)
import {
	buildUrl,
} from '@stonal-tech/lib-front-utils-ts';

// Project modules
import {
	Destination,
	StonalEnv,
} from '@local_types/index';

// Relative imports
import {
	RUDDERSTACK_DESTINATION_NAME,
} from '../config/destinations';
import {
	RUDDERSTACK_QUEUE_OPTIONS,
} from '../config/rudderstack';
import {
	URL_PATTERNS,
} from '../config/urlPatterns';

export class RudderStackDestination implements Destination {
	name: string = RUDDERSTACK_DESTINATION_NAME;
	offlineResilient: boolean = true;
	rudderSourceID: string;
	rudder: RudderAnalytics | undefined;

	constructor(env: StonalEnv) {
		// If we are in prod, we change the source ID
		this.rudder = undefined;
		this.rudderSourceID = env === 'prod' ? '2VGGWzKDDqUE23ygNTOGrjNqYBP' : '2W5NHgy6vV41gEk5Te9wWPqgm2f';
		this.init = this.init.bind(this);
		this.init();
	}

	init() {
		const rudder = new RudderAnalytics();
		rudder.load(this.rudderSourceID, buildUrl(URL_PATTERNS.RUDDER_DATAPLANE), {
			configUrl: buildUrl(URL_PATTERNS.RUDDER_CONFIG),
			// Override the OneTrust consent configured in the RS dashboard.
			// We use a custom provider with full consent granted upfront
			// since no CMP (OneTrust/etc.) is loaded on our apps.
			consentManagement: {
				allowedConsentIds: [
					'Marketing',
				],
				deniedConsentIds: [],
				enabled: true,
				provider: 'custom',
			},
			destSDKBaseURL: buildUrl(URL_PATTERNS.RUDDER_SDK_BASE),
			queueOptions: RUDDERSTACK_QUEUE_OPTIONS,
		});
		this.rudder = rudder;
	}

	page(name: string, properties: Record<string, any>) {
		this.rudder?.page(name, properties);
	}

	identify(userId: string | undefined, traits: Record<string, any>) {
		// Don't send empty/invalid userId - let PostHog handle anonymous users properly
		if (!userId) {
			return;
		}
		this.rudder?.identify(userId, traits);
	}


	group(groupId: string, traits?: Record<string, any>) {
		// Associates the current user with a group (organization/company)
		this.rudder?.group(groupId, traits);
	}

	track(event: string, properties: Record<string, any>) {
		this.rudder?.track(event, properties);
	}
}
